import { useCallback, useEffect, useMemo, useState } from "react"
import { getSafeTypedData, getSortedRows } from "./tableUtils"
import {
  CheckBoxState,
  PaginationProps,
  SortDirection,
  TableHeader,
  TableRow,
} from "./types"
import appInsightsLogger from "../../../logging/appInsightsLogger"
import { useCMSReady } from "../../../../domain/CMS/useCMSReady"
import { populateCmsTemplate } from "../../utils"

export const ROWS_PER_PAGE_OPTIONS = [
  { label: "10", value: 10 },
  { label: "20", value: 20 },
  { label: "30", value: 30 },
  { label: "40", value: 40 },
  { label: "50", value: 50 },
  { label: "60", value: 60 },
  { label: "70", value: 70 },
  { label: "80", value: 80 },
]

interface UseTablePaginationParams {
  rows: TableRow[]
  headers: TableHeader[]
  paginationProps?: PaginationProps
  loading: boolean | undefined
  onCheckboxStateChange?: (checkboxState: CheckBoxState) => void
  pageResetKey?: string | number
}

export function useTablePagination({
  rows,
  headers,
  paginationProps,
  loading,
  onCheckboxStateChange,
  pageResetKey,
}: UseTablePaginationParams) {
  const hasExternalLoading = loading !== undefined
  const {
    cms: { common: commonText },
  } = useCMSReady(["common"])

  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0].value)
  const [loadingEff, setLoadingEff] = useState(loading ?? false)
  const [sortState, setSortState] = useState<{
    col: number | null
    dir: SortDirection | null
  }>({ col: null, dir: null })

  const resetCheckboxState = useCallback(() => {
    onCheckboxStateChange?.(
      rows.reduce((acc, row) => ({ ...acc, [row.data.id]: false }), {}),
    )
  }, [onCheckboxStateChange, rows])

  const handlePageChange = async (page: number) => {
    resetCheckboxState()
    if (paginationProps) {
      setLoadingEff(true)
      try {
        setCurrentPage(page)
        await paginationProps.onPaginationChangeCb(
          rowsPerPage,
          (page - 1) * rowsPerPage,
        )
      } catch (error) {
        appInsightsLogger.traceError("Pagination change failed:", error)
      } finally {
        if (!hasExternalLoading) setLoadingEff(false)
      }
    } else {
      setCurrentPage(page)
    }
  }

  const handleRowsPerPageChange = async (value: number) => {
    resetCheckboxState()
    if (paginationProps) {
      setLoadingEff(true)
      try {
        setRowsPerPage(value)
        setCurrentPage(1)
        await paginationProps.onPaginationChangeCb(value, 0)
      } catch (error) {
        appInsightsLogger.traceError("Rows per page change failed:", error)
      } finally {
        if (!hasExternalLoading) setLoadingEff(false)
      }
    } else {
      setRowsPerPage(value)
      setCurrentPage(1)
    }
  }

  useEffect(() => {
    setLoadingEff(loading ?? false)
  }, [loading])

  // Sync internal pagination state when controlled props are reset externally (e.g. on new search)
  useEffect(() => {
    if (paginationProps?.currentPage !== undefined) {
      setCurrentPage(paginationProps.currentPage)
    }
  }, [paginationProps?.currentPage])

  useEffect(() => {
    if (paginationProps?.rowsPerPage !== undefined) {
      setRowsPerPage(paginationProps.rowsPerPage)
    }
  }, [paginationProps?.rowsPerPage])

  useEffect(() => {
    if (pageResetKey !== undefined) {
      setCurrentPage(1)
    }
  }, [pageResetKey])

  const numberOfPages = useMemo(() => {
    const numberOfEntries =
      typeof paginationProps?.totalCount === "number"
        ? paginationProps.totalCount
        : rows.length
    return Math.ceil(numberOfEntries / rowsPerPage)
  }, [rows.length, paginationProps?.totalCount, rowsPerPage])

  const placeholderText = commonText?.tablePlaceholderText

  const paginatedData = useMemo(() => {
    const safeTypedData = getSafeTypedData(rows, headers, placeholderText)
    let paginated = [...safeTypedData]
    if (!paginationProps) {
      const start = (currentPage - 1) * rowsPerPage
      const end = start + rowsPerPage
      paginated = safeTypedData.slice(start, end)
    }
    return getSortedRows(paginated, sortState, headers)
  }, [
    rows,
    headers,
    currentPage,
    rowsPerPage,
    paginationProps,
    sortState,
    placeholderText,
  ])

  const showingRowsTemplate = commonText?.tableShowingRowsTemplate
  const showingRowsMessage = useMemo(() => {
    if (rows.length === 0) return ""
    const start = (currentPage - 1) * rowsPerPage + 1
    let end = start + rowsPerPage - 1
    const numberOfEntries = paginationProps?.totalCount ?? rows.length
    if (end > numberOfEntries) end = numberOfEntries
    if (loading || numberOfPages === 1) return ""
    return (
      populateCmsTemplate(
        [String(start), String(end), String(numberOfEntries)],
        "cms.common.tableShowingRowsTemplate",
        showingRowsTemplate,
      ) || `${start}-${end} of ${numberOfEntries}`
    )
  }, [
    rows.length,
    currentPage,
    rowsPerPage,
    paginationProps?.totalCount,
    loading,
    numberOfPages,
    showingRowsTemplate,
  ])

  const totalResultsTemplate = commonText?.tableTotalResultsTemplate
  const totalResultsSingularTemplate =
    commonText?.tableTotalResultsSingularTemplate
  const totalResultsMessage = useMemo(() => {
    const numberOfEntries = paginationProps?.totalCount ?? rows.length
    const template =
      numberOfEntries === 1
        ? totalResultsSingularTemplate
        : totalResultsTemplate
    return (
      populateCmsTemplate(
        [String(numberOfEntries)],
        "cms.common.tableTotalResultsTemplate",
        template,
      ) || `Total: ${numberOfEntries} item${numberOfEntries !== 1 ? "s" : ""}`
    )
  }, [
    rows.length,
    paginationProps?.totalCount,
    totalResultsTemplate,
    totalResultsSingularTemplate,
  ])

  return {
    numberOfPages,
    currentPage,
    rowsPerPage,
    loadingEff,
    paginatedData,
    sortState,
    setSortState,
    resetCheckboxState,
    handlePageChange,
    handleRowsPerPageChange,
    showingRowsMessage,
    totalResultsMessage,
    setRowsPerPage,
  }
}
